import { type ComponentProps, type ReactNode, useState } from "react";
import { Animated, Linking, Pressable, Text, View } from "react-native";

import { BrandGlyph } from "@/components/brand/BrandGlyph";
import { ShopLogo } from "@/components/brand/ShopLogo";
import { Sheet, SheetScroll } from "@/components/ui/Sheet";
import { brandFor } from "@/lib/shop/brandLink";
import { shopGameTagLabel } from "@/lib/shop/shopGameTags";
import { slopToFloor } from "@/lib/ui/touchTarget";
import { useThemeColors } from "@/theme/theme";

export type StorefrontLink = { label: string; url: string; };

/**
 * The OS large-text bound on the shop's name, the same 1.3 `StockRow` bounds its quiet line at.
 *
 * The phone row is the same height at both its sizes only while the name's line box stays under the
 * floor the row and a seated Follow set. Unbounded, very large system text clears that floor at the
 * resting `text-xl` before it does at the bar's `text-base`, so the header would shrink as the page
 * collapsed and shift every card below it.
 */
const NAME_MAX_SCALE = 1.3;

/**
 * The shop's front door above its inventory (TRO-1171): who this is, what they sell, where else to
 * find them.
 *
 * Every field here is published under ADR-0137's test — a vendor edits all of them on
 * `ShopIdentitySection` — and every one is optional, so a shop that has filled none of them renders
 * exactly what it rendered before rather than a scaffold of empty rows.
 *
 * On a phone it is two rows: the logo, the name and the Follow control, then one line of tagline and
 * area beside an About link to a sheet holding the tags, the paragraph and the links, so the cards
 * start near the top of the page; a desktop page shows everything inline. Game tags are labels, not
 * controls: the shop's Game pills own narrowing.
 *
 * The phone's first row is one mounted component at two sizes. Given `row` it stays on screen as a
 * bar while the page scrolls, the tagline line and About going behind it, and `bar` shrinks it once
 * the header has collapsed; it is the same height either way, so nothing below it moves.
 */
export function StorefrontIdentity(
  {
    name,
    logoUrl,
    tagline,
    bio,
    operatingArea,
    tags,
    links,
    wide,
    subtitle,
    follow,
    back,
    row,
    bar = false,
  }: {
    name: string;
    logoUrl?: string | null;
    tagline?: string;
    bio?: string;
    operatingArea?: string;
    tags?: readonly string[];
    links?: readonly StorefrontLink[];
    wide: boolean;
    /** The desktop meta line's count; a phone shows the count beside its filters instead. */
    subtitle: string;
    /** The phone's Follow control, seated at the end of the name row. */
    follow?: ReactNode;
    /**
     * A back control leading the name row, before the logo, so it stays on screen in the pinned
     * bar. The app passes one; web passes none.
     */
    back?: ReactNode;
    /** The phone name row's wrapper props from `useCollapsingHeader().sticky`. */
    row?: Pick<ComponentProps<typeof Animated.View>, "onLayout" | "style">;
    /** The phone name row at its smaller, pinned size. */
    bar?: boolean;
  },
) {
  const [aboutOpen, setAboutOpen] = useState(false);
  const meta = [operatingArea?.trim(), subtitle].filter(Boolean).join(" · ");
  const claim = tagline?.trim() ?? "";
  // Vendors often open the bio with the tagline again; printing it twice reads as a mistake.
  const bioText = withoutLeadingLine(bio?.trim() ?? "", claim);
  const about = <About tags={tags} bio={bioText} links={links} />;
  const hasAbout = !!(tags?.length || bioText || links?.length);

  const nameRow = (
    <View className="flex-row items-center gap-3">
      {back}
      <ShopLogo name={name} uri={logoUrl} size={80} />
      <View className="min-w-0 flex-1">
        <Text
          accessibilityRole="header"
          // react-native-web renders an unleveled `header` as `<h1>`; state the level explicitly.
          aria-level={1}
          className="text-2xl font-title text-foreground"
          numberOfLines={1}
        >
          {name}
        </Text>
        {claim
          ? (
            <Text
              className="text-sm font-body text-foreground"
              numberOfLines={2}
            >
              {claim}
            </Text>
          )
          : null}
        <Text className="text-sm font-body text-muted-foreground" numberOfLines={1}>{meta}</Text>
      </View>
    </View>
  );

  // A desktop page has the room, so it shows everything inline.
  if (wide) {
    return (
      <View className="gap-3">
        {nameRow}
        {about}
      </View>
    );
  }

  // A phone shows two rows, so the cards start near the top, and keeps the tags, the paragraph and
  // the links one tap away in a sheet. About is a sibling of the line, not part of it: at this width
  // a long tagline can fill the line, and the sheet is the only route to the rest.
  const line = [claim, operatingArea?.trim()].filter(Boolean).join(" · ");
  return (
    <View className="gap-2">
      <Animated.View {...row}>
        <View testID="storefront-name-row" className="min-h-11 flex-row items-center gap-3 bg-bg">
          {back}
          <ShopLogo name={name} uri={logoUrl} size={bar ? 26 : 44} />
          <Text
            accessibilityRole="header"
            aria-level={1}
            className={bar
              ? "min-w-0 flex-1 text-base font-title text-foreground"
              : "min-w-0 flex-1 text-xl font-title text-foreground"}
            maxFontSizeMultiplier={NAME_MAX_SCALE}
            numberOfLines={1}
          >
            {name}
          </Text>
          {follow}
        </View>
      </Animated.View>
      {line || hasAbout
        ? (
          <View className="flex-row items-center gap-3">
            {line
              ? (
                <Text
                  className="min-w-0 flex-1 text-sm font-body text-muted-foreground"
                  numberOfLines={1}
                >
                  {line}
                </Text>
              )
              : null}
            {hasAbout
              ? (
                <Pressable
                  testID="storefront-about"
                  accessibilityRole="button"
                  accessibilityLabel={`About ${name}`}
                  hitSlop={slopToFloor(44)}
                  className="ml-auto flex-none"
                  onPress={() => setAboutOpen(true)}
                >
                  <Text className="text-sm font-label text-primary">About ›</Text>
                </Pressable>
              )
              : null}
          </View>
        )
        : null}
      <Sheet visible={aboutOpen} onClose={() => setAboutOpen(false)} title={name}>
        <SheetScroll contentContainerClassName="gap-4 pb-2">
          {claim ? <Text className="text-base font-body text-foreground">{claim}</Text> : null}
          {about}
        </SheetScroll>
      </Sheet>
    </View>
  );
}

/** The tags, the paragraph and the links, inline on desktop and in the phone's About sheet. */
function About(
  { tags, bio, links }: {
    tags?: readonly string[];
    bio: string;
    links?: readonly StorefrontLink[];
  },
) {
  const colors = useThemeColors();
  return (
    <>
      {tags?.length
        ? (
          <View className="flex-row flex-wrap gap-2" accessibilityRole="list">
            {tags.map((tag) => (
              <Text
                key={tag}
                // A list's children must be its items (axe `aria-required-children`).
                role="listitem"
                className="rounded-lg bg-surface-muted px-2.5 py-1 font-label text-xs text-muted-foreground"
              >
                {shopGameTagLabel(tag)}
              </Text>
            ))}
          </View>
        )
        : null}

      {bio
        ? (
          // Capped at 500 characters on the way in, which is already a paragraph, not a page.
          <Text className="font-body text-sm leading-5 text-muted-foreground">{bio}</Text>
        )
        : null}

      {links?.length
        ? (
          <View className="flex-row flex-wrap gap-2">
            {links.map((link) => (
              <Pressable
                key={`${link.label}:${link.url}`}
                onPress={() => void Linking.openURL(link.url)}
                accessibilityRole="link"
                // Icon only, so the label carries the whole accessible name — and a mark that is
                // itself a wordmark (eBay) does not print its own name twice beside it.
                accessibilityLabel={link.label}
                hitSlop={slopToFloor(44)}
                className="size-11 items-center justify-center rounded-full border border-border bg-surface"
              >
                <BrandGlyph name={brandFor(link.url)} color={colors.foreground} />
              </Pressable>
            ))}
          </View>
        )
        : null}
    </>
  );
}

/** The text without its first line when that line repeats `line`. */
function withoutLeadingLine(text: string, line: string): string {
  if (!line) return text;
  const [first, ...rest] = text.split("\n");
  return first.trim() === line ? rest.join("\n").trim() : text;
}
